'use client'

import React, { useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@material-tailwind/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons'
import { ParsedIngredient } from '@/lib/recipe-import/types'

interface IngredientMatchingModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (ingredients: ParsedIngredient[]) => void
  parsedIngredients: ParsedIngredient[]
  recipeTitle: string
}

const IngredientMatchingModal: React.FC<IngredientMatchingModalProps> = ({
  open,
  onClose,
  onConfirm,
  parsedIngredients,
  recipeTitle,
}) => {
  const [ingredients, setIngredients] = useState<ParsedIngredient[]>(parsedIngredients)

  const formatQuantity = (ingredient: ParsedIngredient): string => {
    const { whole, numerator, denominator } = ingredient.quantity

    if (numerator === 0) {
      return whole.toString()
    }

    if (whole === 0) {
      return `${numerator}/${denominator}`
    }

    return `${whole} ${numerator}/${denominator}`
  }

  const handleConfirm = () => {
    onConfirm(ingredients)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[700px] max-h-[80vh] overflow-y-auto bg-white p-6 font-inter">
        <div className="space-y-4">
          {/* Header */}
          <div className="border-b border-gray-200 pb-4">
            <h2 className="text-2xl font-bold text-gray-900">Review Ingredients</h2>
            <p className="mt-2 text-sm text-gray-600">
              {recipeTitle}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              We've parsed {parsedIngredients.length} ingredients from the recipe. Review them below.
            </p>
          </div>

          {/* Ingredients List */}
          <div className="space-y-3">
            {ingredients.map((ingredient, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4 hover:border-gray-300 transition-colors"
              >
                {/* Original Text */}
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-700">
                    Original:
                  </p>
                  <p className="text-sm text-gray-900 italic">
                    "{ingredient.originalText}"
                  </p>
                </div>

                {/* Parsed Components */}
                <div className="grid grid-cols-4 gap-3 text-sm">
                  {/* Quantity */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Quantity
                    </p>
                    <p className="text-gray-900 font-medium">
                      {formatQuantity(ingredient)}
                    </p>
                  </div>

                  {/* Unit */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Unit
                    </p>
                    <p className="text-gray-900 font-medium">
                      {ingredient.uom?.unit_name ||
                       ingredient.uom?.unit_abbreviation ||
                       <span className="text-gray-400">-</span>}
                    </p>
                  </div>

                  {/* Ingredient */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Ingredient
                    </p>
                    <p className="text-gray-900 font-medium">
                      {ingredient.ingredientName || <span className="text-gray-400">-</span>}
                    </p>
                  </div>

                  {/* Modifier */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Modifier
                    </p>
                    <p className="text-gray-700 text-xs">
                      {ingredient.modifier || <span className="text-gray-400">-</span>}
                    </p>
                  </div>
                </div>

                {/* Note about new ingredients */}
                {ingredient.ingredientName && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <FontAwesomeIcon icon={faPlus} className="h-3 w-3 text-[#85C349]" />
                    <span className="text-gray-600">
                      Will be created as new ingredient (requires admin approval)
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0"
              />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Ready to import
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  These ingredients will be added to your recipe. You can edit them further on the recipe form.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <Button
              onClick={handleConfirm}
              className="flex-1 rounded-full bg-[#85C349] px-6 py-3 text-sm font-medium text-white hover:bg-[#75B339]"
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2 h-4 w-4" />
              Confirm & Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default IngredientMatchingModal
